import { Slot } from '@radix-ui/react-slot';
import { cn, cva } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import * as React from 'react';

const buttonVariants = cva(
    'user-select-none inline-flex items-center justify-center text-md font-medium whitespace-nowrap ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden enabled:hover:cursor-pointer disabled:pointer-events-none disabled:cursor-not-allowed disabled:brightness-50 disabled:saturate-75 aria-disabled:cursor-not-allowed data-disabled:cursor-not-allowed',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-primary/90',
                primary: 'bg-steel-500 text-gray-900 hover:bg-steel-500/90',
                destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                outline:
                    'border border-gray-200 bg-background hover:bg-accent hover:text-accent-foreground dark:border-gray-700',
                secondary: 'bg-gray-800 text-white hover:bg-gray-800/80',
                ghost: 'hover:bg-accent hover:text-accent-foreground',
                link: 'text-foreground underline-offset-4 hover:underline dark:text-primary',
                positive: 'bg-jade-600 text-white hover:bg-jade-700 active:bg-jade-800',
                negative: 'bg-ember-450 text-white hover:bg-ember-600 active:bg-ember-700',
                positiveOutline:
                    'border border-jade-600 bg-jade-950/30 text-jade-500 hover:border-jade-700 active:border-jade-800 active:text-jade-600',
                negativeOutline:
                    'border border-ember-450 bg-ember-950/40 text-ember-500 hover:border-ember-600 active:border-ember-700 active:text-ember-600',
                light: 'bg-white text-gray-700',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 px-3',
                xs: 'h-8 px-3',
                '2xs': 'h-6 px-2.5 text-sm',
                lg: 'h-11 px-8',
                icon: 'h-10 w-10',
                smIcon: 'h-6 w-6',
            },
            rounding: {
                default: 'rounded-md',
                sm: 'rounded-xs',
                lg: 'rounded-lg',
                xl: 'rounded-xl',
                full: 'rounded-full',
                none: 'rounded-none',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
            rounding: 'default',
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, rounding, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return <Comp className={cn(buttonVariants({ variant, size, rounding, className }))} ref={ref} {...props} />;
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
